"use client"

import React, { useState, useRef, useEffect, useMemo } from "react"
import {
  Bell,
  X,
  MessageSquare,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Trash2,
  CheckCheck,
  Sparkles,
  Filter,
} from "@/lib/icons"
import { TYPE, PRIORITY } from "@/hooks/useNotifications"
import { Button } from "@/components/ui/button"
import { NotificationSound } from "@/components/notifications/notification-sound"
import { Badge } from "@/components/ui/badge"

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) return `hace ${diffDays}d`
  if (diffHours > 0) return `hace ${diffHours}h`
  if (diffMins > 0) return `hace ${diffMins}m`
  return "ahora"
}

const TYPE_META = {
  [TYPE.MESSAGE]: {
    label: "Mensaje",
    icon: MessageSquare,
    iconBg: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
  },
  [TYPE.INACTIVE]: {
    label: "Sin actividad",
    icon: AlertTriangle,
    iconBg: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200",
  },
  [TYPE.FOLLOW_UP]: {
    label: "Seguimiento",
    icon: Clock,
    iconBg: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-200",
  },
  [TYPE.SCHEDULED]: {
    label: "Agendado",
    icon: Calendar,
    iconBg: "bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-200",
  },
  [TYPE.SUCCESS]: {
    label: "Completado",
    icon: CheckCircle2,
    iconBg: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-200",
  },
  [TYPE.ASSIGNMENT]: {
    label: "Asignación",
    icon: Bell,
    iconBg: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-200",
  },
  [TYPE.STAGE_CHANGE]: {
    label: "Cambio",
    icon: Bell,
    iconBg: "bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-200",
  },
  [TYPE.EXAM_READY]: {
    label: "Examen",
    icon: CheckCircle2,
    iconBg: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-200",
  },
  default: {
    label: "Notificación",
    icon: Bell,
    iconBg: "bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-200",
  },
}

const CATEGORY_CONFIG = [
  {
    id: "messages",
    label: "Mensajes",
    description: "Respuestas recientes de tus leads",
    types: [TYPE.MESSAGE],
    accent: {
      dot: "bg-amber-500",
      iconBg: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
      rowClass: "bg-amber-50/70 border border-amber-100/70 dark:bg-amber-950/10 dark:border-amber-900/30 shadow-sm",
      chipClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-100",
    },
  },
  {
    id: "follow",
    label: "Seguimiento",
    description: "Recordatorios y compromisos",
    types: [TYPE.FOLLOW_UP, TYPE.SCHEDULED],
    accent: {
      dot: "bg-purple-500",
      iconBg: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-200",
      rowClass: "bg-purple-50/70 border border-purple-100/70 dark:bg-purple-950/10 dark:border-purple-900/30 shadow-sm",
      chipClass: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-100",
    },
  },
  {
    id: "inactive",
    label: "Sin actividad",
    description: "Leads rezagados",
    types: [TYPE.INACTIVE],
    accent: {
      dot: "bg-orange-500",
      iconBg: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-200",
      rowClass: "bg-orange-50/70 border border-orange-100/70 dark:bg-orange-950/10 dark:border-orange-900/30 shadow-sm",
      chipClass: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-100",
    },
  },
  {
    id: "leads",
    label: "Nuevos leads",
    description: "Asignaciones y cambios",
    types: [TYPE.ASSIGNMENT, TYPE.STAGE_CHANGE, TYPE.EXAM_READY],
    accent: {
      dot: "bg-emerald-500",
      iconBg: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-200",
      rowClass: "bg-emerald-50/80 border border-emerald-100/70 dark:bg-emerald-950/10 dark:border-emerald-900/30 shadow-sm",
      chipClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100",
    },
  },
  {
    id: "other",
    label: "Otros",
    description: "Actualizaciones generales",
    types: null,
    accent: {
      dot: "bg-slate-400",
      iconBg: "bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-200",
      rowClass: "bg-white/80 border border-slate-200/80 dark:bg-slate-900/40 dark:border-slate-800/60 shadow-sm",
      chipClass: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200",
    },
  },
]

const PRIORITY_COLOR = {
  [PRIORITY.CRITICAL]: "bg-red-500",
  [PRIORITY.HIGH]: "bg-amber-500",
  [PRIORITY.MEDIUM]: "bg-indigo-500",
  [PRIORITY.LOW]: "bg-slate-400",
}

const NotificationRow = ({ notification, onMarkAsRead, onDismiss, onClick, accent }) => {
  const typeMeta = TYPE_META[notification.type] || TYPE_META.default
  const Icon = typeMeta.icon
  const bullet = PRIORITY_COLOR[notification.priority] || PRIORITY_COLOR[PRIORITY.LOW]
  const iconBg =
    accent?.iconBg || typeMeta.iconBg || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
  const unreadRow =
    accent?.rowClass ||
    "bg-white/90 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 shadow-sm"
  const rowClass = notification.read
    ? "border border-slate-100 dark:border-slate-800"
    : unreadRow
  const dotColor = notification.read ? "bg-slate-300" : accent?.dot || bullet

  const handleClick = () => {
    if (!notification.read) onMarkAsRead?.(notification.id)
    onClick?.(notification)
  }

  return (
    <div className={`flex items-start gap-2 sm:gap-3 py-3 px-3 sm:py-4 sm:px-4 rounded-xl sm:rounded-2xl transition active:scale-[0.98] cursor-pointer ${rowClass}`} onClick={handleClick}>
      <span className={`mt-2 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full flex-shrink-0 ${dotColor}`} />
      <div className={`flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full flex-shrink-0 ${iconBg}`}>
        <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`font-medium text-xs sm:text-sm line-clamp-1 ${notification.read ? "text-slate-500" : "text-slate-900 dark:text-white"}`}>
            {notification.title}
          </p>
          <span className="text-[10px] sm:text-xs text-slate-400 whitespace-nowrap flex-shrink-0">{formatTimeAgo(notification.createdAt)}</span>
        </div>
        <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1 line-clamp-2">{notification.message}</p>
      </div>
      <div className="flex flex-col gap-1 sm:gap-2 flex-shrink-0">
        {!notification.read && (
          <button
            className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 active:scale-95 transition-transform"
            onClick={(e) => {
              e.stopPropagation()
              onMarkAsRead?.(notification.id)
            }}
            title="Marcar como leída"
          >
            <CheckCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          </button>
        )}
        <button
          className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 active:scale-95 transition-transform"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss?.(notification.id)
          }}
          title="Descartar"
        >
          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
      </div>
    </div>
  )
}

export function NotificationPanel({
  notifications = [],
  unreadCount = 0,
  loading = false,
  playSoundType = null,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismiss,
  onClose,
  onNotificationClick,
}) {
  const [visible, setVisible] = useState(false)
  const [filterUnread, setFilterUnread] = useState(false)
  const panelRef = useRef(null)

  const filteredNotifications = useMemo(
    () => (filterUnread ? notifications.filter((n) => !n.read) : notifications),
    [notifications, filterUnread],
  )

  const sortedNotifications = useMemo(() => {
    return [...filteredNotifications].sort((a, b) => {
      const aDate = new Date(a.createdAt || 0).getTime()
      const bDate = new Date(b.createdAt || 0).getTime()
      return bDate - aDate
    })
  }, [filteredNotifications])

  const groupedByCategory = useMemo(() => {
    const fallbackId =
      CATEGORY_CONFIG.find((config) => !config.types)?.id ||
      CATEGORY_CONFIG[CATEGORY_CONFIG.length - 1].id
    const base = CATEGORY_CONFIG.reduce((acc, config) => {
      acc[config.id] = []
      return acc
    }, {})

    sortedNotifications.forEach((notification) => {
      const match = CATEGORY_CONFIG.find((config) =>
        config.types ? config.types.includes(notification.type) : false,
      )
      const target = match ? match.id : fallbackId
      base[target].push(notification)
    })

    return base
  }, [sortedNotifications])

  const categorySummaries = useMemo(
    () =>
      CATEGORY_CONFIG.map((config) => ({
        id: config.id,
        label: config.label,
        count: groupedByCategory[config.id]?.length || 0,
        accent: config.accent,
      })),
    [groupedByCategory],
  )

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50)
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        handleClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      clearTimeout(timer)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleClose = () => {
    setVisible(false)
    setTimeout(() => onClose(), 300)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-start justify-center sm:justify-end">
      {playSoundType && <NotificationSound play={true} type={playSoundType} />}

      <div
        ref={panelRef}
        className={`w-full sm:w-[420px] h-[90vh] sm:h-full bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-l-3xl sm:rounded-tr-none shadow-2xl flex flex-col border border-slate-200 dark:border-slate-800 transform transition-all duration-300 ease-out ${visible ? "translate-y-0 sm:translate-x-0 opacity-100" : "translate-y-full sm:translate-y-0 sm:translate-x-full opacity-0"}`}
      >
        {/* Mobile drag indicator */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-300 dark:bg-slate-700 rounded-full" />
        </div>

        <div className="p-4 sm:p-5 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl sm:rounded-2xl bg-slate-900 text-white dark:bg-white/10 dark:text-white flex items-center justify-center">
                <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white">Notificaciones</h3>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {unreadCount > 0 ? `${unreadCount} sin leer` : "Nada pendiente"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 active:scale-95 transition-transform"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-2 mt-3 sm:mt-4">
            <button
              onClick={() => setFilterUnread(!filterUnread)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${filterUnread
                ? "bg-slate-900 text-white dark:bg-white/10"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                }`}
            >
              <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {filterUnread ? "Todas" : "Sin leer"}
            </button>

            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onMarkAllAsRead}
                className="text-xs sm:text-sm font-semibold h-8 sm:h-9 px-2 sm:px-3"
              >
                <CheckCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Marcar</span> todas
              </Button>
            )}
          </div>

          {/* Category chips - Scrollable on mobile */}
          <div className="flex gap-1.5 sm:gap-2 mt-3 sm:mt-4 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap scrollbar-hide">
            {categorySummaries.map((summary) => (
              <div
                key={summary.id}
                className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] sm:text-xs font-semibold whitespace-nowrap flex-shrink-0 ${summary.accent.chipClass ||
                  "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                  }`}
              >
                <span className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${summary.accent.dot}`} />
                <span>{summary.label}</span>
                <span>{summary.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 space-y-4">
              <div className="animate-pulse h-16 w-16 rounded-full bg-slate-200/80 dark:bg-slate-700/50" />
              <div className="animate-pulse h-4 w-40 rounded-md bg-slate-200/80 dark:bg-slate-700/50" />
              <div className="w-full max-w-xs space-y-3 pt-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                    <div className="animate-pulse h-8 w-8 rounded-lg bg-slate-200/80 dark:bg-slate-700/50 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="animate-pulse h-3 w-full rounded-md bg-slate-200/80 dark:bg-slate-700/50" />
                      <div className="animate-pulse h-2.5 w-2/3 rounded-md bg-slate-200/80 dark:bg-slate-700/50" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : sortedNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="relative">
                <div className="h-28 w-28 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 dark:from-indigo-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-full flex items-center justify-center shadow-lg">
                  <Sparkles className="h-14 w-14 text-indigo-500 dark:text-indigo-400" />
                </div>
                <div className="absolute -top-2 -right-2 h-8 w-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
              </div>
              <h4 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-2">¡Todo limpio!</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 max-w-xs">
                No tienes notificaciones pendientes. Sigue así gestionando tus leads de forma increíble.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {CATEGORY_CONFIG.map((category) => {
                const items = groupedByCategory[category.id] || []
                if (!items.length) return null

                // Determinamos cuántos items sin leer hay en esta categoría
                const unreadInCategory = items.filter(n => !n.read).length

                // Componente local para manejar el estado de cada acordeón
                return (
                  <CategoryAccordion
                    key={category.id}
                    category={category}
                    items={items}
                    unreadCount={unreadInCategory}
                    onMarkAsRead={onMarkAsRead}
                    onDismiss={onDismiss}
                    onNotificationClick={onNotificationClick}
                  />
                )
              })}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900/70 border-t border-slate-200 dark:border-slate-800 safe-area-bottom">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs">
                <div className="flex items-center gap-1">
                  <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-indigo-500" />
                  <span className="font-semibold text-slate-700 dark:text-slate-200">Total: {notifications.length}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-emerald-500" />
                  <span className="text-slate-600 dark:text-slate-400">Leídas: {notifications.length - unreadCount}</span>
                </div>
              </div>
              {unreadCount > 0 && (
                <Badge className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0 text-[10px] sm:text-xs px-2 py-0.5">
                  {unreadCount} sin leer
                </Badge>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// NUEVO: Componente interno para manejar el expandir/contraer de cada categoría
function CategoryAccordion({ category, items, unreadCount, onMarkAsRead, onDismiss, onNotificationClick }) {
  // Por defecto, si tiene no leídos, que inicie abierto; si está todo leído, que inicie cerrado.
  const [isOpen, setIsOpen] = useState(unreadCount > 0)

  // Si cambian los items y de repente aparecen no leídas, volvemos a abrir si estaba cerrado
  useEffect(() => {
    if (unreadCount > 0) {
      setIsOpen(true)
    }
  }, [unreadCount])

  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 sm:p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`flex items-center justify-center p-2 rounded-xl bg-gradient-to-br ${category.id === 'messages' ? 'from-amber-100 to-orange-100 text-amber-600 dark:from-amber-900/50 dark:to-orange-900/50 dark:text-amber-400'
            : category.id === 'follow' ? 'from-purple-100 to-fuchsia-100 text-purple-600 dark:from-purple-900/50 dark:to-fuchsia-900/50 dark:text-purple-400'
              : category.id === 'inactive' ? 'from-orange-100 to-red-100 text-orange-600 dark:from-orange-900/50 dark:to-red-900/50 dark:text-orange-400'
                : category.id === 'leads' ? 'from-emerald-100 to-teal-100 text-emerald-600 dark:from-emerald-900/50 dark:to-teal-900/50 dark:text-emerald-400'
                  : 'from-slate-100 to-gray-100 text-slate-600 dark:from-slate-800 dark:to-gray-800 dark:text-slate-400'
            }`}>
            {category.id === 'messages' && <MessageSquare className="h-4 w-4" />}
            {category.id === 'follow' && <Clock className="h-4 w-4" />}
            {category.id === 'inactive' && <AlertTriangle className="h-4 w-4" />}
            {category.id === 'leads' && <CheckCircle2 className="h-4 w-4" />}
            {category.id === 'other' && <Bell className="h-4 w-4" />}
          </div>
          <div className="text-left">
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">{category.label}</h4>
            <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">{category.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-end">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{items.length} {items.length === 1 ? "aviso" : "avisos"}</span>
            {unreadCount > 0 && (
              <span className="text-[10px] text-red-500 font-bold">{unreadCount} nuevos</span>
            )}
          </div>
          <div className={`p-1.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </div>
        </div>
      </button>

      {/* Contenido Expandible */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
      >
        <div className="p-3 sm:p-4 pt-1 sm:pt-2 bg-slate-50/50 dark:bg-slate-900/30 border-t border-slate-100 dark:border-slate-800 space-y-2.5">
          {items.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              onMarkAsRead={onMarkAsRead}
              onDismiss={onDismiss}
              onClick={onNotificationClick}
              accent={category.accent}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

